import { cn } from '../../../lib/utils';
import { ZoomResetButton } from '../../climbs/photo-editor/ZoomResetButton';
import type { RouteHoldsController } from './useRouteHolds';

interface RouteHoldsCanvasProps {
  editor: RouteHoldsController;
  imageUrl: string;
  /** How a circle is named to a screen reader, given the hold number it wears. */
  holdLabel: (number: number) => string;
  resetZoomLabel: string;
  /**
   * What a plain click on a circle means to the page. The editor has none — a
   * press there selects, and that is the controller's business — while the
   * scoring dialog uses it to pick the hold reached.
   */
  onPick?: (number: number) => void;
  /** The hold number the page is standing on, drawn as the chosen one. */
  picked?: number | null;
  testId?: string;
  className?: string;
}

/**
 * A lead route's photo with its holds circled and numbered.
 *
 * One surface for both jobs, because they are the same picture: the admin draws
 * the route on it (`RouteHoldsDialog`) and the judge reads a card off it
 * (`LeadScoringDialog`), and a judge tapping hold 23 has to be looking at exactly
 * what the admin drew.
 *
 * The frame is the photo's own box — the `<img>` sizes it and keeps its
 * proportions at any width — so a fraction of the photo is a percentage of this
 * element and the circles need no projection. The picture and the circles sit in
 * one transformed layer, so zooming moves both and they cannot drift apart.
 *
 * Which circle a press means is the frame's to decide (see `useRouteHolds`),
 * never the circle's: that is what lets the topmost of a stack win. The buttons
 * are here to be hit-testable — a circle the browser cannot see is a circle whose
 * drag it hands to the page as a scroll — and to give the keyboard a way in.
 */
export function RouteHoldsCanvas({
  editor,
  imageUrl,
  holdLabel,
  resetZoomLabel,
  onPick,
  picked,
  testId,
  className,
}: RouteHoldsCanvasProps) {
  const { zoom, holds, selected, select, readOnly } = editor;

  return (
    <div
      {...zoom.frameProps}
      // The editor's gestures run after the zoom's, which only records pointers
      // for the pinch; a press that turns out to be on a circle takes the pan
      // back itself (`cancelPan`).
      onPointerDown={(event) => {
        zoom.frameProps.onPointerDown(event);
        editor.frameProps.onPointerDown(event);
      }}
      onPointerMove={(event) => {
        zoom.frameProps.onPointerMove(event);
        editor.frameProps.onPointerMove(event);
      }}
      onPointerUp={(event) => {
        zoom.frameProps.onPointerUp(event);
        editor.frameProps.onPointerUp(event);
      }}
      onPointerCancel={(event) => {
        zoom.frameProps.onPointerCancel(event);
        editor.frameProps.onPointerCancel(event);
      }}
      data-testid={testId}
      className={cn(
        'relative w-full select-none overflow-hidden rounded-md bg-muted',
        readOnly ? 'cursor-pointer' : 'cursor-crosshair',
        className
      )}
    >
      <div {...zoom.layerProps}>
        <img src={imageUrl} alt="" className="block w-full" draggable={false} />

        {holds.map((hold, index) => {
          const number = index + 1;
          const isPicked = picked === number;

          return (
            <button
              key={index}
              type="button"
              aria-pressed={selected === index || isPicked}
              aria-label={holdLabel(number)}
              data-testid="route-hold"
              data-hold-number={number}
              onClick={(event) => {
                // A mouse click is already answered by the frame, so this is the
                // keyboard's way in (`detail === 0` is Enter or Space) — except
                // where the page wants the click itself, which is the judge
                // picking a hold off the picture.
                if (onPick) {
                  event.stopPropagation();
                  onPick(number);
                  select(index);
                  return;
                }
                if (event.detail === 0) select(index);
              }}
              className={cn(
                'absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px]',
                isPicked
                  ? 'border-primary bg-primary/30'
                  : selected === index
                    ? 'border-white ring-2 ring-white ring-offset-1 ring-offset-black/40'
                    : 'border-yellow-400'
              )}
              style={{
                left: `${hold.x * 100}%`,
                top: `${hold.y * 100}%`,
                width: `${hold.r * 200}%`,
                aspectRatio: '1 / 1',
                touchAction: 'none',
              }}
            >
              {/* The hold's number — which is only ever where the circle sits.
                  White with a shadow under it: the one colour that reads on a
                  photograph of any wall. */}
              <span
                className={cn(
                  'whitespace-nowrap font-bold leading-none text-white',
                  number > 99 ? 'text-[8px]' : 'text-[10px]'
                )}
                style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}
              >
                {number}
              </span>
            </button>
          );
        })}
      </div>

      <ZoomResetButton zoom={zoom} label={resetZoomLabel} />
    </div>
  );
}
